class Person {


  _name: string;
  _birth_date: Date;  // JSON serialization converts Date to string.
  _retirement_age: number;
  _max_age: number;  // how far are we going to take our model?
  _private_pension_age: number;  // when you are allowed to start withdrawing
  _final_salary_pension_start_age: number; // payments start at this age
  _final_salary_pension_pa: number;  // payment per year
  _simulation_start_year: number;
  _critical_quantile_key: number;  // how conservative

  constructor(
    name: string,
    birth_date: Date,
    retirement_age: number,
    max_age: number,
    private_pension_age: number,
    final_salary_pension_start_age: number,
    final_salary_pension_pa: number,
    simulation_start_year: number,
    critical_quantile_key: number,
  ) {
    this._name = name;
    this._birth_date = birth_date;
    this._retirement_age = retirement_age;
    this._max_age = max_age;
    this._private_pension_age = private_pension_age;
    this._final_salary_pension_start_age = final_salary_pension_start_age;
    this._final_salary_pension_pa = final_salary_pension_pa;
    this._simulation_start_year = simulation_start_year;
    this._critical_quantile_key = critical_quantile_key;
  }

  /**
   * Returns a plain object.
   * CRITICAL: No JSON.stringify() here.
   */
  serialize(): any {
    return {
      name: this.name,
      // Ensure Date becomes a string for the transport
      birth_date: this.birth_date instanceof Date ? this.birth_date.toISOString() : this.birth_date,
      retirement_age: this.retirement_age,
      max_age: this.max_age,
      private_pension_age: this.private_pension_age,
      final_salary_pension_start_age: this.final_salary_pension_start_age,
      final_salary_pension_pa: this.final_salary_pension_pa,
      simulation_start_year: this.simulation_start_year,
      critical_quantile_key: this.critical_quantile_key
    };
  }

  /**
   * Accepts the object produced by the Master JSON.parse call.
   */
  static deserialize(data: any): Person {
    if (!data) throw new Error("Person.deserialize: No data");

    // Defensive check in case a string sneaks in
    const j = typeof data === 'string' ? JSON.parse(data) : data;

    return new Person(
      j.name,
      new Date(j.birth_date), // Hydrate the string back into a Date object
      j.retirement_age,
      j.max_age,
      j.private_pension_age,
      j.final_salary_pension_start_age,
      j.final_salary_pension_pa,
      j.simulation_start_year,
      j.critical_quantile_key
    );
  }

  clone(): Person {
    return new Person(
      this._name,
      this.birth_date,
      this._retirement_age,
      this._max_age,
      this._private_pension_age,
      this._final_salary_pension_start_age,
      this._final_salary_pension_pa,
      this._simulation_start_year,
      this._critical_quantile_key,
    );
  }

  get name(): string {
    return this._name;
  }

  get birth_date(): Date {
    return this._birth_date;
  }

  get retirement_age(): number {
    return this._retirement_age;
  }

  get max_age(): number {
    return this._max_age;
  }

  get max_year(): number {
    return this.birth_date.getUTCFullYear() + this.max_age;
  }

  get private_pension_age(): number {
    return this._private_pension_age;
  }

  get final_salary_pension_start_age(): number {
    return this._final_salary_pension_start_age;
  }

  get final_salary_pension_pa(): number {
    return this._final_salary_pension_pa;
  }

  get simulation_start_year(): number {
    return this._simulation_start_year;
  }

  get critical_quantile_key(): number {
    return this._critical_quantile_key;
  }

  set_retirement_age(retirement_age: number): Person {
    return new Person(
      this._name,
      this.birth_date,
      retirement_age,
      this._max_age,
      this._private_pension_age,
      this._final_salary_pension_start_age,
      this._final_salary_pension_pa,
      this._simulation_start_year,
      this._critical_quantile_key,
    );
  }

  set_retirement_year(year: number): Person {
    const ms: number = new Date(year, 4, 6).getTime() - this.birth_date.getTime();
    const retirement_age = ms / (1000 * 60 * 60 * 24 * 365.25);
    return this.set_retirement_age(retirement_age);
  }

  get retirement_date(): Date {
    return new Date(
      this.birth_date.getFullYear() + this._retirement_age,
      this.birth_date.getMonth(),
      this.birth_date.getDate(),
    );
  }
}

export { Person };
